import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonDatetime,
  IonDatetimeButton,
  IonFooter,
  IonHeader,
  IonIcon,
  IonLabel,
  IonModal,
  IonPopover,
  IonRow,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Organization } from 'src/models/organization';
import { ControllerMessages } from 'src/intl/ControllerMessages';
import { JobMessages } from 'src/intl/JobMessages';
import { close } from 'ionicons/icons';
import './FilterJobsModal.scss';
import { useHasTouchscreen } from 'src/utils/helpers/platformHelpers';
import { InputSelect } from 'src/components/reusable-components/InputSelect';

interface FilterJobsProps {
  /**
   * The Organizations options for the controller filter.
   */
  organizations: Organization[];
  /**
   * The physical serial of the controller options.
   */
  controllerPhysicalSerials: string[];
  /**
   * Whether the modal is open.
   */
  isOpen: boolean;
  /**
   * Triggers the request to close the modal and reset the state.
   */
  onClose: () => void;
  /**
   * Triggers the request to close the modal and save the state object.
   */
  onSave: (e: FilterJobsModalState) => void;
}

export interface FilterJobsModalState {
  controller: string | undefined;
  organization: string | undefined;
  startDate: string | undefined;
  endDate: string | undefined;
}

const filterInitialState: FilterJobsModalState = {
  organization: undefined,
  controller: undefined,
  startDate: new Date('05 October 2020 14:48 UTC').toISOString(),
  endDate: new Date().toISOString(),
};

export const FilterJobsModal: React.FC<FilterJobsProps> = ({
  organizations,
  controllerPhysicalSerials,
  onClose,
  isOpen,
  onSave,
}) => {
  const [filterSelections, setFilterSelections] = useState<FilterJobsModalState>(filterInitialState);
  const { formatMessage } = useIntl();
  const hasTouchscreen = useHasTouchscreen();

  useEffect(() => {
    setFilterSelections(filterInitialState);
  }, [isOpen]);

  const organizationSelectOptions = organizations.map(org => {
    return {
      label: org.orgName,
      value: org._id,
    };
  });

  const physicalSerialSelectOptions = controllerPhysicalSerials.map(serial => {
    return {
      label: serial,
      value: `${formatMessage(JobMessages.controller)} ${serial}`,
    };
  });

  return (
    <IonModal className="filter-jobs-modal" isOpen={isOpen} onDidDismiss={() => onClose()}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Filter</IonTitle>
          <IonButtons slot="start">
            <IonButton onClick={() => onClose()}>
              <IonIcon size="large" color="dark" slot="icon-only" icon={close} onClick={() => onClose()} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <InputSelect<string>
          label={formatMessage(ControllerMessages.organization)}
          options={organizationSelectOptions}
          value={filterSelections.organization}
          onChange={orgId => setFilterSelections({ ...filterSelections, organization: orgId })}
          placeholder={formatMessage(JobMessages.selectAnOrganization)}
          id="filter-jobs-select-organization"
        />

        <InputSelect<string>
          label={formatMessage(JobMessages.controller)}
          value={filterSelections.controller}
          onChange={controllerId => setFilterSelections({ ...filterSelections, controller: controllerId })}
          placeholder={formatMessage(JobMessages.selectAController)}
          options={physicalSerialSelectOptions}
          id="filter-jobs-select-controller"
        />
        <IonRow className="date-row">
          <IonCol size="6" className="ion-no-padding">
            <IonLabel className="date-time-label">{formatMessage(JobMessages.startDate)}</IonLabel>
            <IonDatetimeButton datetime="start-date" className="date-time" />
            <IonPopover keepContentsMounted={true} size="auto">
              <IonDatetime
                multiple={false}
                id="start-date"
                presentation="date"
                preferWheel={hasTouchscreen}
                value={filterSelections.startDate}
                onIonChange={e => setFilterSelections({ ...filterSelections, startDate: e.detail.value?.toString() })}
              />
            </IonPopover>
          </IonCol>
          <IonCol size="6" className="ion-no-padding">
            <IonLabel className="date-time-label">{formatMessage(JobMessages.endDate)}</IonLabel>
            <IonDatetimeButton datetime="end-date" className="date-time" />
            <IonPopover keepContentsMounted={true} size="auto">
              <IonDatetime
                id="end-date"
                multiple={false}
                presentation="date"
                preferWheel={hasTouchscreen}
                value={filterSelections.endDate}
                onIonChange={e => setFilterSelections({ ...filterSelections, endDate: e.detail.value?.toString() })}
              />
            </IonPopover>
          </IonCol>
        </IonRow>
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <IonButton onClick={() => onSave(filterSelections)} fill="solid" expand="block" color="primary">
            {formatMessage(JobMessages.filterJobs)}
          </IonButton>
        </IonToolbar>
      </IonFooter>
    </IonModal>
  );
};

export default FilterJobsModal;
